import { useEffect, useState, useCallback } from 'react';
import { Command } from 'cmdk';
import { useNavigate } from 'react-router';
import {
  Search, Users, Building2, Tag, Package, Plus, LayoutDashboard, Keyboard,
} from 'lucide-react';
import { api } from '../services/api';

const TYPE_BADGE = {
  lead:    { label: 'Lead',     cls: 'bg-blue-500/20 text-blue-300' },
  conta:   { label: 'Conta',    cls: 'bg-green-500/20 text-green-300' },
  user:    { label: 'Usuário',  cls: 'bg-purple-500/20 text-purple-300' },
  product: { label: 'Produto',  cls: 'bg-orange-500/20 text-orange-300' },
};

const TYPE_ICON = {
  lead:    Tag,
  conta:   Building2,
  user:    Users,
  product: Package,
};

export default function CommandPalette({ open, onClose, onNewLead, onNewConta, onShowShortcuts }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  const doSearch = useCallback(async (q) => {
    if (!q.trim()) {
      setResults([]);
      return;
    }
    setLoading(true);
    try {
      const data = await api.search(q);
      setResults(Array.isArray(data) ? data : (data.results || []));
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => doSearch(query), 200);
    return () => clearTimeout(t);
  }, [query, doSearch]);

  const handleSelect = useCallback((item) => {
    navigate(item.url);
    onClose();
  }, [navigate, onClose]);

  if (!open) return null;

  const grouped = results.reduce((acc, r) => {
    const key = r.type || 'other';
    if (!acc[key]) acc[key] = [];
    acc[key].push(r);
    return acc;
  }, {});

  const groupOrder = ['lead', 'conta', 'user', 'product'];
  const groupLabel = { lead: 'Leads', conta: 'Contas', user: 'Equipe', product: 'Produtos' };

  return (
    <div
      className="fixed inset-0 bg-black/60 z-[200] flex items-start justify-center pt-[15vh]"
      onClick={onClose}
    >
      <div
        className="bg-[#002233] border border-[#00DBFF]/20 rounded-xl shadow-2xl w-full max-w-xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <Command shouldFilter={false} onKeyDown={(e) => { if (e.key === 'Escape') onClose(); }}>
          <div className="flex items-center gap-2 px-3 border-b border-white/10">
            <Search size={16} className="text-white/40 shrink-0" />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Buscar leads, contas, usuários…"
              className="flex-1 bg-transparent text-white placeholder-white/40 py-3 text-sm outline-none"
              autoFocus
            />
            {loading && (
              <span className="text-white/30 text-xs shrink-0">…</span>
            )}
          </div>

          <Command.List className="max-h-[60vh] overflow-y-auto p-1">
            <Command.Empty className="py-8 text-center text-white/30 text-sm">
              {query ? 'Nenhum resultado encontrado.' : 'Digite para buscar…'}
            </Command.Empty>

            <Command.Group>
              <div className="text-[10px] uppercase text-white/30 px-3 py-1 tracking-wider">Ações</div>

              <Command.Item
                onSelect={() => { onNewLead(); onClose(); }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 rounded cursor-pointer hover:bg-[#00DBFF]/10 hover:text-white aria-selected:bg-[#00DBFF]/10 aria-selected:text-white"
              >
                <Plus size={14} className="text-[#00DBFF]" />
                Novo lead
              </Command.Item>

              <Command.Item
                onSelect={() => { onNewConta(); onClose(); }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 rounded cursor-pointer hover:bg-[#00DBFF]/10 hover:text-white aria-selected:bg-[#00DBFF]/10 aria-selected:text-white"
              >
                <Building2 size={14} className="text-[#00DBFF]" />
                Nova conta
              </Command.Item>

              <Command.Item
                onSelect={() => { navigate('/dashboard'); onClose(); }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 rounded cursor-pointer hover:bg-[#00DBFF]/10 hover:text-white aria-selected:bg-[#00DBFF]/10 aria-selected:text-white"
              >
                <LayoutDashboard size={14} className="text-white/50" />
                Dashboard
              </Command.Item>

              <Command.Item
                onSelect={() => { onClose(); onShowShortcuts?.(); }}
                className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 rounded cursor-pointer hover:bg-[#00DBFF]/10 hover:text-white aria-selected:bg-[#00DBFF]/10 aria-selected:text-white"
              >
                <Keyboard size={14} className="text-white/50" />
                Atalhos de teclado
              </Command.Item>
            </Command.Group>

            {groupOrder.filter((g) => grouped[g]?.length).map((type) => (
              <Command.Group key={type}>
                <div className="text-[10px] uppercase text-white/30 px-3 py-1 tracking-wider mt-1">
                  {groupLabel[type]}
                </div>
                {grouped[type].map((item) => {
                  const Icon = TYPE_ICON[type] || Tag;
                  const badge = TYPE_BADGE[type];
                  return (
                    <Command.Item
                      key={`${type}-${item.id}`}
                      value={`${type}-${item.id}-${item.label}`}
                      onSelect={() => handleSelect(item)}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-white/80 rounded cursor-pointer hover:bg-[#00DBFF]/10 hover:text-white aria-selected:bg-[#00DBFF]/10 aria-selected:text-white"
                    >
                      <Icon size={14} className="text-white/40 shrink-0" />
                      <span className="flex-1 truncate">{item.title}</span>
                      {badge && (
                        <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${badge.cls}`}>
                          {badge.label}
                        </span>
                      )}
                    </Command.Item>
                  );
                })}
              </Command.Group>
            ))}
          </Command.List>
        </Command>
      </div>
    </div>
  );
}
